import {hasher} from 'hasher';
import {_} from 'lodash';
import {
    IRouteDefinition,
    IRouteDefinitionParams,
    IRouteParams,
    TRouteId,
    TRouteIdOrDefinition,
} from '../index';
import {_d, isNullOrUndefined} from '../util/misc';
import {global} from '../appContext';
import {getListRouteForDetail, getRouteDefinition} from './routeLookup';

export function getChildHash(def: IRouteDefinition, params?: IRouteParams, withHash = true): string {
    if (!def.child) {
        return getHash(def.id, params ?? {}, withHash) ?? '';
    }
    const childDef = getRouteDefinition(def.child);
    if (childDef) {
        const detailHash = getPageShellDetailHash(childDef, params ?? {}, withHash);
        if (detailHash) {
            return detailHash;
        }
    }
    return getHash(def.child, params ?? {}, withHash) ?? '';
}

/** Hash for a PageShell object-detail route (bare shell — tab memory adjudicates on entry). */
function getPageShellDetailHash(
    def: IRouteDefinition,
    params: IRouteParams = {},
    withHash = false,
): string | null {
    if (def.component?.name !== 'component/page-shell/details' || !def.tabs?.length || !def.hash) {
        return null;
    }
    return getHash(def, params, withHash);
}

export function getParentHash(
    def: IRouteDefinition,
    params?: IRouteParams,
    withHash = true,
): IRouteDefinition['hash'] | null {
    if (!def.parent) {
        _d.error(`No parent defined for ${def.id}`, def);
        return null;
    }
    return getHash(def.parent, params, withHash);
}

export function setRouteHash(idOrDef: TRouteIdOrDefinition, params?: IRouteParams) {
    const def = getRouteDefinition(idOrDef);
    if (!def) {
        return;
    }
    /* formlogin uten params skal ikke arve orgnr/index fra gjeldende rute (da blir feks URL hengende etter «Bytt organisasjon»). */
    const effectiveParams =
        params === undefined && def.id === 'formlogin' ? {} : params;
    const hash = getPageShellDetailHash(def, effectiveParams ?? {}, false) ?? getHash(def, effectiveParams ?? {}, false);
    if (hash == null) {
        return;
    }
    // Object detail pages always push — replace would drop the search/list step from browser history.
    if (def.isSubitem) {
        hasher.setHash(hash);
        return;
    }
    // Auth targets replace so empty → formlogin / logout transitions do not stack history.
    const currentRoute = global.router.currentRoute();
    if (
        def.id === 'formlogin'
        || def.id === 'logout'
        || currentRoute?.__routeDef?.storeHash === false
    ) {
        hasher.replaceHash(hash);
    } else {
        hasher.setHash(hash);
    }
}

/**
 * About → Application when an org is selected; bare `#/application` otherwise
 * (footer/logo stay usable before login / without org).
 */
export function getApplicationHash(withHash = true): string {
    const orgnr = global.login?.selectedOrganizationId();
    if (orgnr) {
        return getHash('about_application', {orgnr}, withHash) ?? (withHash ? '#/application' : 'application');
    }
    return getHash('application', {}, withHash) ?? (withHash ? '#/application' : 'application');
}

export function getHash(
    idOrDef: TRouteIdOrDefinition,
    params: IRouteParams | any = global.router.currentRoute()?.__routeParams ?? {},
    withHash = true,
    orgnr?: string,
): string | null {
    const hash = getRouteDefinition(idOrDef)?.hash;
    if (!hash) {
        _d.error('No hash for given route idOrDef', idOrDef);
        return null;
    }
    const strHash = ((): string => {
        if (typeof hash === 'string') {
            return hash;
        }
        const arr = hash as string[];
        const hasOrg =
            params &&
            ((params.orgnr != null && String(params.orgnr) !== '') ||
                (params.orgid != null && String(params.orgid) !== ''));
        if (hasOrg) {
            /* Prefer the formlogin pattern that carries orgnr when org is set */
            const withOrg = arr.find(
                (h) =>
                    typeof h === 'string' &&
                    /formlogin/i.test(h) &&
                    (h.includes(':orgnr:') || h.includes('{orgnr}')) &&
                    !/^o\//i.test(h),
            );
            if (withOrg) {
                return withOrg;
            }
        }
        return arr[0] ?? '';
    })();
    return replaceHashParams(strHash, params, withHash, orgnr);
}

export type TRouteDefinitionDestination = 'create' | 'createWizard' | 'read' | 'edit' | 'child' | 'parent';

export function getRouteHash(
    type: TRouteDefinitionDestination | TRouteDefinitionDestination[],
    def: IRouteDefinition | null = global.router.currentRoute()?.__routeDef ?? null,
    params?: IRouteParams,
    withHash = true,
    obj: any = {},
): string | null {
    if (!def) {
        _d.warn('No route definition given.');
        return null;
    }
    const ret: Record<TRouteDefinitionDestination, TRouteId | undefined> = {
        create: def.create,
        createWizard: def.createWizard,
        read: def.id,
        edit: def.edit,
        child: def.child,
        parent: def.parent,
    };
    if (Array.isArray(type)) {
        const t = type.shift();
        if (!t) {
            return null;
        }
        const routeId = ret[t];
        if (!routeId) {
            return getHash(def, _.defaultsDeep(obj, params, {orgnr: global.login.selectedOrganizationId()}), withHash);
        }
        const d = getRouteDefinition(routeId);
        if (!d) {
            return null;
        }
        return getRouteHash(type.length ? type : t, d, params, withHash, obj);
    }
    const target = ret[type];
    return getHash(target ? target : def, _.defaultsDeep(obj, params, {orgnr: global.login.selectedOrganizationId()}), withHash);
}

function replaceHashParams(
    hash: string,
    params: IRouteParams,
    withHash: boolean,
    orgnr?: string,
): string {
    const effectiveOrgnr = orgnr ?? global.login?.selectedOrganizationId() ?? '';
    if (params === undefined) {
        _d.error('No params object defined for hash replacement', hash);
    }
    const rp: IRouteParams = params ?? {};
    if (hash.includes('orgnr')) {
        rp['orgnr'] = rp['orgnr'] ?? rp['orgid'] ?? effectiveOrgnr;
    }
    const keys = Object.keys(rp) as (keyof IRouteParams)[];
    let ret = hash;
    keys.forEach((k) => {
        const regExpKey = new RegExp(`[{|:]${k}[:|}]`, 'gi');
        if (regExpKey.test(ret)) {
            if (!isNullOrUndefined(rp[k])) {
                const v = String(rp[k]);
                ret = ret.replace(regExpKey, v ?? '');
            }
        }
    });
    const regExpKeyOptional = new RegExp(`\/:.*:`, 'gi');
    if (regExpKeyOptional.test(ret)) {
        ret = ret.replace(regExpKeyOptional, '');
    }

    if (withHash) {
        ret = '#/' + ret;
    }
    return ret;
}

/**
 * Navigate after the user switches organization.
 * Pushes a history entry so Back restores the previous org URL.
 * List/overview URLs keep the same path with the new org; detail URLs go to the section list.
 */
export function navigateForOrgChange(newOrgId: string, cr: IRouteDefinitionParams): void {
    if (!cr?.__routeDef) {
        return;
    }
    // Most details use {id}; import history uses {importfileid} (see getPageShellNavigationKey).
    const objectId = cr.__id ?? cr.__routeParams?.['id'] ?? cr.__routeParams?.['importfileid'];
    if (objectId == null || String(objectId) === '') {
        const hash = hasher.getHash();
        if (/^o\/[^/]+(\/|$)/.test(hash)) {
            hasher.setHash(hash.replace(/^o\/[^/]+/, `o/${newOrgId}`));
            return;
        }
        const routeDef = cr.__tabRouteDef
            ?? (cr.__routeId && cr.__routeId !== cr.__routeDef.id ? getRouteDefinition(cr.__routeId) : cr.__routeDef);
        if (routeDef) {
            const nextHash = getHash(routeDef, {orgnr: newOrgId}, false);
            if (nextHash != null) {
                hasher.setHash(nextHash);
            }
        }
        return;
    }
    const detailDef = cr.__shellRouteDef ?? cr.__routeDef;
    const listHash = getHash(getListRouteForDetail(detailDef), {orgnr: newOrgId}, false);
    if (listHash != null) {
        hasher.setHash(listHash);
    }
}
